import type { SkillDefinition, SkillRun } from '../types'
import { API_BASE, authHeaders } from './apiAuth'

/**
 * The skills, read from the platform's own list
 * -------------------------------------------------------------
 * This used to return a TypeScript constant. The constant is gone.
 *
 * The reason it had to go: the Worker could not read it — separate builds —
 * so the assistant could only be told what this platform does by copying the
 * list into a prompt. Two copies of one list is a promise that they will
 * disagree, and the copy that disagrees silently would have been the prompt:
 * the model recommending, confidently, a skill that had been renamed or
 * dropped.
 *
 * Now there is one list, in `public.skill_definitions`, and the website, the
 * Miro panel and the model all read it.
 */

/** Skills kept in the table but not offered yet (2026-10-19). */
const HIDDEN_SKILLS = new Set(['two-by-two-map'])

export interface SkillService {
  listSkills(): Promise<SkillDefinition[]>
  getSkill(slug: string): Promise<SkillDefinition | null>
  /** Validate a selection against the skill's input spec. Nothing executes. */
  dryRun(skillId: string, assetIds: string[]): Promise<SkillRun>
}

export class ApiSkillService implements SkillService {
  async listSkills(): Promise<SkillDefinition[]> {
    const response = await fetch(`${API_BASE}/api/skills`, {
      headers: { Accept: 'application/json', ...(await authHeaders()) },
    })
    if (!response.ok) throw new Error(`Could not read the skills (HTTP ${response.status})`)
    // Not shown anywhere for now -- the skills page, the canvas, the panel
    // (Huanrui, 2026-10-19: later): its row stays in the table.
    return ((await response.json()) as SkillDefinition[]).filter((skill) => !HIDDEN_SKILLS.has(skill.slug))
  }

  async getSkill(slug: string): Promise<SkillDefinition | null> {
    const all = await this.listSkills()
    return all.find((skill) => skill.slug === slug) ?? null
  }

  async dryRun(skillId: string, assetIds: string[]): Promise<SkillRun> {
    const all = await this.listSkills()
    const skill = all.find((s) => s.id === skillId)
    if (!skill) {
      throw new Error(`Unknown skill: ${skillId}`)
    }

    const assetInput = skill.inputs.find((i) => i.type === 'assets')
    if (assetInput) {
      const min = assetInput.minAssets ?? 1
      const max = assetInput.maxAssets ?? Infinity
      if (assetIds.length < min) {
        throw new Error(
          `${skill.name} needs at least ${min} image${min === 1 ? '' : 's'}. You selected ${assetIds.length}.`,
        )
      }
      if (assetIds.length > max) {
        throw new Error(
          `${skill.name} accepts at most ${max} images. You selected ${assetIds.length}.`,
        )
      }
    }

    return {
      id: `run-${Date.now()}`,
      skillId,
      userId: 'local-dev',
      status: 'queued',
      inputAssetIds: assetIds,
      params: {},
      outputAssetIds: [],
      error: null,
      createdAt: new Date().toISOString(),
      finishedAt: null,
    }
  }
}

let service: SkillService | null = null

export function getSkillService(): SkillService {
  if (!service) service = new ApiSkillService()
  return service
}
